import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useSearchParams } from 'react-router-dom';
import DetailPanel from '../components/DetailPanel';
import { useAuth } from '../AuthContext';
import { OPERATIONS_TABS } from '../config/pages';

const PLATFORM_COLOR = {
  Facebook:'#1877f2', Instagram:'#e1306c', LinkedIn:'#0a66c2',
  YouTube:'#ff0000', WhatsApp:'#25d366', Twitter:'#000', Pinterest:'#bd081c',
};

const STATUS_MAP = {
  Draft:              { label:'✍️ Draft',              color:'var(--accent)' },
  Rejected:           { label:'❌ Rejected',            color:'#e84545' },
  'Pending Internal': { label:'🔒 Pending Internal',   color:'#e87830' },
  'Pending Client':   { label:'👔 Pending Client',     color:'#6c58f0' },
  Approved:           { label:'✅ Approved',            color:'var(--green)' },
};

const TAB_CONFIG = [
  { id:'draft',    label:'✍️ Draft',              statuses:['Draft','Rejected'],      color:'var(--accent)' },
  { id:'internal', label:'🔒 Internal Approval',  statuses:['Pending Internal'],       color:'#e87830' },
  { id:'client',   label:'👔 Client Approval',    statuses:['Pending Client'],         color:'#6c58f0' },
  { id:'approved', label:'✅ Approved',            statuses:['Approved'],               color:'var(--green)' },
];

const EMPTY_MSG = {
  draft:    { icon:'✍️', msg:'No post copies in draft',          sub:'Active work orders with a Post Copy phase will appear here.' },
  internal: { icon:'🔒', msg:'No copies pending internal review', sub:'Submit copies from the Draft tab.' },
  client:   { icon:'👔', msg:'No copies pending client approval', sub:'Internally approved copies will appear here.' },
  approved: { icon:'✅', msg:'No approved copies yet',            sub:'Approved copies will appear here.' },
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function VersionHistory({ versions }) {
  const [open, setOpen] = useState(false);
  if (!versions?.length) return null;
  const actionStyle = {
    'Submitted':         { color:'var(--accent)', icon:'📤' },
    'Approved Directly': { color:'var(--green)',  icon:'✅' },
    'Sent to Client':    { color:'#6c58f0',       icon:'👔' },
    'Client Approved':   { color:'var(--green)',  icon:'✅' },
    'Rejected Internal': { color:'var(--red)',    icon:'↩' },
    'Rejected Client':   { color:'var(--red)',    icon:'↩' },
  };
  return (
    <div style={{marginTop:'14px'}}>
      <div onClick={()=>setOpen(v=>!v)} style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',padding:'6px 0',borderTop:'1px solid var(--border)'}}>
        <span style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text3)'}}>📜 Version History</span>
        <span style={{fontSize:'0.65rem',color:'var(--text3)'}}>{versions.length} submission{versions.length!==1?'s':''}</span>
        <span style={{marginLeft:'auto',fontSize:'0.65rem',color:'var(--text3)'}}>{open?'▼':'▶'}</span>
      </div>
      {open && (
        <div>
          {versions.map((v,i)=>{
            const st = actionStyle[v.action]||{color:'var(--text3)',icon:'•'};
            return (
              <div key={i} style={{display:'flex',gap:'10px',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0}}>
                  <div style={{width:'26px',height:'26px',borderRadius:'50%',background:st.color+'22',border:`2px solid ${st.color}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.7rem',fontWeight:700,color:st.color}}>{v.version}</div>
                  {i<versions.length-1 && <div style={{width:'2px',flex:1,minHeight:'10px',background:'var(--border)',marginTop:'2px'}} />}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap',marginBottom:'2px'}}>
                    <span style={{fontSize:'0.72rem',fontWeight:700,color:st.color}}>{st.icon} {v.action}</span>
                    <span style={{fontSize:'0.68rem',color:'var(--text3)',fontFamily:'monospace'}}>{fmtDate(v.at)}</span>
                  </div>
                  {v.rejection_reason && <div style={{fontSize:'0.75rem',color:'var(--red)',padding:'3px 8px',background:'#ff000008',borderRadius:'4px'}}>↩ {v.rejection_reason}</div>}
                  {v.caption && <div style={{fontSize:'0.72rem',color:'var(--text2)',marginTop:'2px',whiteSpace:'pre-wrap'}}>{v.caption.length>120?v.caption.slice(0,120)+'…':v.caption}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StageBanner({ task }) {
  if (!task) return null;
  const st = task.status;
  if (st==='In Progress') return (
    <div style={{marginBottom:'12px',padding:'8px 14px',background:'#1db87a10',border:'1px solid #1db87a33',borderLeft:'4px solid var(--green)',borderRadius:'8px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
      <span style={{width:'9px',height:'9px',borderRadius:'50%',background:'var(--green)',display:'inline-block'}} />
      <span style={{fontSize:'0.78rem',fontWeight:700,color:'#0f8050'}}>● Working{task.started_at?` — since ${fmtDate(task.started_at)}`:''}</span>
      <span style={{padding:'2px 10px',background:'var(--bg2)',border:'1px solid var(--border2)',borderRadius:'12px',fontSize:'0.72rem',fontWeight:700}}>👤 {task.assignee||'Unassigned'}</span>
    </div>
  );
  if (st==='Blocked') return (
    <div style={{marginBottom:'12px',padding:'8px 14px',background:'#e8454512',border:'1px solid #e8454540',borderLeft:'4px solid var(--red)',borderRadius:'8px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
      <span style={{fontSize:'0.78rem',fontWeight:700,color:'var(--red)'}}>⛔ ON HOLD: {task.hold_reason||'—'}</span>
      <span style={{padding:'2px 10px',background:'var(--bg2)',border:'1px solid var(--border2)',borderRadius:'12px',fontSize:'0.72rem',fontWeight:700}}>👤 {task.assignee||'Unassigned'}</span>
    </div>
  );
  return (
    <div style={{marginBottom:'12px',padding:'12px 16px',border:'1px solid #ff6c3744',borderLeft:'4px solid #ff6c37',borderRadius:'10px',display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap'}}>
      <span style={{padding:'6px 16px',background:'#ff6c37',color:'#fff',borderRadius:'8px',fontWeight:800,fontSize:'0.82rem',cursor:'pointer'}}>▶ START WORK</span>
      <span style={{padding:'3px 11px',background:'#fff',border:'1px solid #ff6c3744',borderRadius:'14px',fontSize:'0.74rem',fontWeight:800,color:'#a8451e'}}>👤 {task.assignee||'Unassigned'}</span>
      <span style={{fontSize:'0.8rem',color:'#7a4632'}}><b style={{color:'#a8451e'}}>Ready for you.</b> Press Start so the team sees you're on it.</span>
    </div>
  );
}

function PostCopyCard({ entry, postCopy, tab, onSave, onSubmit, onApproveInternal, onApproveDirectly, onSendToClient, onApproveClient, onReject, onActivate, isActive }) {
  const pc = postCopy || {};
  const status = pc.status || 'Draft';
  const accentColor = STATUS_MAP[status]?.color || 'var(--accent)';
  const isDraft      = status==='Draft' || status==='Rejected';
  const isInternal   = status==='Pending Internal';
  const isPendClient = status==='Pending Client';
  const isReadonly   = status==='Approved';

  const [caption,       setCaption]      = useState(pc.caption       || '');
  const [hashtags,      setHashtags]     = useState(pc.hashtags      || '');
  const [link,          setLink]         = useState(pc.link          || '');
  const [suggestedTime, setSuggestedTime]= useState(pc.suggested_time|| '');
  const [platformNotes, setPlatformNotes]= useState(pc.platform_notes|| '');
  const [rejectReason,  setRejectReason] = useState('');
  const [saving,        setSaving]       = useState(false);

  const [ey,em,ed] = (entry.event_date||'').split('-');

  const getData = () => ({ caption, hashtags, link, suggested_time: suggestedTime, platform_notes: platformNotes });

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(entry.id, getData()); } finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!caption.trim()) { alert('Please write the post copy before submitting.'); return; }
    await handleSave();
    await onSubmit(entry.id);
  };

  const handleReject = async (stage) => {
    if (!rejectReason.trim()) { alert('Please enter a rejection reason.'); return; }
    await onReject(entry.id, rejectReason, stage);
    setRejectReason('');
  };

  return (
    <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderLeft:`4px solid ${accentColor}`,borderRadius:'var(--radius)',marginBottom:'16px',overflow:'hidden'}}>
      {/* Header */}
      <div style={{padding:'10px 16px',background:'var(--bg3)',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
        <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--accent)',fontSize:'0.78rem'}}>{entry.wo_number||'—'}</span>
        <span className="badge badge-blue">{entry.client_name||entry.client}</span>
        <span style={{fontWeight:700,fontSize:'0.85rem'}}>{entry.title}</span>
        {entry.event_date && <span style={{fontSize:'0.72rem',color:'var(--text3)'}}>📅 <b>Event:</b> {ed}-{em}-{ey}</span>}
        {entry.campaign_type && <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--accent)'}}>🎯 {entry.campaign_type}</span>}
        <span style={{marginLeft:'auto',fontSize:'0.72rem',fontWeight:700,color:accentColor}}>{STATUS_MAP[status]?.label||status}</span>
        {onActivate && (
          <button onClick={onActivate} title={isActive?'Close panel':'View details'}
            style={{width:26,height:26,borderRadius:6,border:`1px solid ${isActive?'var(--accent)':'var(--border)'}`,background:isActive?'rgba(79,124,247,0.12)':'var(--bg2)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:isActive?'var(--accent)':'var(--text3)'}}>
            <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor"><circle cx="8" cy="5" r="1.2"/><rect x="7" y="7" width="2" height="5" rx="1"/><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
          </button>
        )}
      </div>

      <div style={{padding:'14px 16px'}}>
        {/* Platforms */}
        {entry.platforms?.length > 0 && (
          <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'10px'}}>
            {entry.platforms.map(p=>(
              <span key={p} style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'2px 8px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'6px',fontSize:'0.72rem',fontWeight:600,color:PLATFORM_COLOR[p]||'var(--text2)'}}>
                {p}
              </span>
            ))}
          </div>
        )}

        {/* Script brief reference */}
        {entry.script && (entry.script.hook || entry.script.script) && (
          <div style={{padding:'10px 12px',background:'var(--bg3)',borderRadius:'8px',marginBottom:'14px',borderLeft:'3px solid var(--accent)'}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',marginBottom:'5px',textTransform:'uppercase',letterSpacing:'0.5px'}}>📋 Script Brief (Reference)</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',fontSize:'0.78rem'}}>
              {entry.script.hook && <div><span style={{color:'var(--text3)'}}>Caption: </span>{entry.script.hook}</div>}
              {entry.script.cta  && <div><span style={{color:'var(--text3)'}}>CTA: </span>{entry.script.cta}</div>}
            </div>
          </div>
        )}

        {/* Stage strip */}
        {isDraft && entry.task && <StageBanner task={entry.task} />}

        {/* Rejection note */}
        {pc.rejection_reason && isDraft && (
          <div style={{padding:'8px 12px',background:'#ff000010',border:'1px solid #ff000030',borderRadius:'6px',fontSize:'0.78rem',color:'var(--red)',marginBottom:'12px'}}>↩ Rejected: {pc.rejection_reason}</div>
        )}

        {/* Copy fields — editable */}
        {!isReadonly && (
          <>
            <div className="form-group" style={{marginBottom:'10px'}}>
              <label className="form-label">✍️ Post Caption / Copy</label>
              <textarea className="form-textarea" value={caption} onChange={e=>setCaption(e.target.value)}
                style={{height:'100px',lineHeight:'1.6'}} placeholder="Full post caption with emojis and hashtags..." readOnly={isInternal||isPendClient} />
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'10px'}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">#️⃣ Hashtags</label>
                <input className="form-input" value={hashtags} onChange={e=>setHashtags(e.target.value)}
                  placeholder="#worldenvironmentday #sustainability" readOnly={isInternal||isPendClient} />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">🔗 Link in Bio / URL</label>
                <input className="form-input" value={link} onChange={e=>setLink(e.target.value)}
                  placeholder="https://..." readOnly={isInternal||isPendClient} />
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'12px'}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">🕐 Suggested Post Time</label>
                <input type="time" className="form-input" value={suggestedTime} onChange={e=>setSuggestedTime(e.target.value)} readOnly={isInternal||isPendClient} />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">📢 Platform Notes</label>
                <input className="form-input" value={platformNotes} onChange={e=>setPlatformNotes(e.target.value)}
                  placeholder="Platform-specific tweaks..." readOnly={isInternal||isPendClient} />
              </div>
            </div>
          </>
        )}

        {/* Read-only approved view */}
        {isReadonly && (
          <div style={{padding:'12px',background:'var(--bg3)',borderRadius:'8px',marginBottom:'12px'}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',marginBottom:'6px',textTransform:'uppercase'}}>✍️ APPROVED COPY</div>
            <div style={{fontSize:'0.85rem',whiteSpace:'pre-wrap',lineHeight:'1.6'}}>{pc.caption||'—'}</div>
            {pc.hashtags && <div style={{marginTop:'6px',fontSize:'0.78rem',color:'#6c58f0'}}>{pc.hashtags}</div>}
            {pc.link     && <div style={{marginTop:'4px',fontSize:'0.75rem'}}><a href={pc.link} target="_blank" rel="noreferrer" style={{color:'var(--accent)'}}>🔗 {pc.link}</a></div>}
            {pc.suggested_time && <div style={{marginTop:'4px',fontSize:'0.75rem',color:'var(--text3)'}}>🕐 Post at: {pc.suggested_time}</div>}
          </div>
        )}

        {/* Rejection input for review tabs */}
        {(isInternal||isPendClient) && (
          <div style={{marginBottom:'12px'}}>
            <label className="form-label" style={{fontSize:'0.72rem'}}>Rejection Notes (required to reject)</label>
            <input className="form-input" value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="Describe what needs to change..." />
          </div>
        )}

        {/* Action buttons */}
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          {isDraft && <>
            <button onClick={handleSave} disabled={saving} className="btn btn-secondary" style={{fontSize:'0.78rem'}}>{saving?'…':'💾 Save'}</button>
            <button onClick={handleSubmit} disabled={saving} className="btn btn-primary" style={{fontSize:'0.78rem',background:'#e87830',borderColor:'#e87830'}}>🔒 Submit for Internal Review</button>
          </>}
          {isInternal && <>
            <button onClick={()=>onApproveDirectly(entry.id)} style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'var(--green)',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>✅ Approve Directly (Skip Client)</button>
            <button onClick={()=>onSendToClient(entry.id)} style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'#6c58f0',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>👔 Approve & Send to Client</button>
            <button onClick={()=>handleReject('internal')} style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'var(--red)',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>↩ Reject</button>
          </>}
          {isPendClient && <>
            <button onClick={()=>onApproveClient(entry.id)} style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'var(--green)',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>✅ Client Approved</button>
            <button onClick={()=>handleReject('client')} style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'var(--red)',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer'}}>↩ Reject</button>
          </>}
          {isReadonly && <span style={{fontSize:'0.78rem',fontWeight:600,color:'var(--green)'}}>✅ Approved</span>}
        </div>

        <VersionHistory versions={pc.versions||[]} />
      </div>
    </div>
  );
}

export default function PostCopy() {
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const urlWo = searchParams.get("wo");
  const { user } = useAuth();
  const { canTab } = useAuth();
  const allowedPostCopyTabs = TAB_CONFIG
    .filter(t => { const cfg = (OPERATIONS_TABS.postcopy||[]).find(x=>x.id===t.id); return !cfg?.permissionKey || canTab(cfg.permissionKey); });
  const [tab,      setTab]      = useState(urlTab && allowedPostCopyTabs.find(t=>t.id===urlTab) ? urlTab : (allowedPostCopyTabs[0]?.id || 'draft'));
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [activeWOId, setActiveWOId] = useState(null);
  const [urlWoHandled, setUrlWoHandled] = useState(false);
  const [yearF,    setYearF]    = useState('');
  const [monthsF,  setMonthsF]  = useState(new Set());
  const [showMonthDd,setShowMonthDd]= useState(false);
  const [clientF,  setClientF]  = useState('');
  const [woFilter, setWoFilter] = useState('');
  const [wftF,     setWftF]     = useState('');
  const [hidePosted, setHidePosted] = useState(true);
  const [clients,  setClients]  = useState([]);
  const [wftypes,  setWftypes]  = useState([]);
  const [counts,   setCounts]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientF) params.client_id = clientF;
      if (wftF)    params.campaign_type_id = wftF;
      const [entRes, clRes, wfRes] = await Promise.all([
        api.get('/post-copy', { params }),
        api.get('/clients'),
        api.get('/campaign-types'),
      ]);
      const all = entRes.data.data || [];
      setEntries(all);
      setClients(clRes.data.data || clRes.data || []);
      setWftypes(wfRes.data.data || wfRes.data || []);
      const c = { draft:0, internal:0, client:0, approved:0 };
      all.forEach(e=>{
        if (woFilter && !(e.wo_number||'').toLowerCase().includes(woFilter.toLowerCase())) return false;
    const st=e.post_copy?.status||'Draft';
        if(st==='Draft'||st==='Rejected'||!e.post_copy) c.draft++;
        if(st==='Pending Internal')                      c.internal++;
        if(st==='Pending Client')                        c.client++;
        if(st==='Approved')                              c.approved++;
      });
      setCounts(c);
    } catch(e){ console.error(e); }
    finally{ setLoading(false); }
  }, [clientF, wftF]);

  useEffect(()=>{ load(); }, [load]);

  // Auto-open WO from URL param
  useEffect(() => {
    if (urlWo && entries.length > 0 && !urlWoHandled) {
      const match = entries.find(e => e.wo_number === urlWo || String(e.id) === urlWo);
      if (match) { setActiveWOId(match.id); }
      setUrlWoHandled(true);
    }
  }, [urlWo, entries, urlWoHandled]);

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(String);
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const toggleMonth = m => setMonthsF(prev => { const s = new Set(prev); s.has(m) ? s.delete(m) : s.add(m); return s; });

  useEffect(() => {
    if (!showMonthDd) return;
    const h = e => { if (!e.target.closest('[data-month-dd]')) setShowMonthDd(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMonthDd]);

  const approvedCount = entries.filter(e => {
    const st = e.post_copy?.status || 'Draft';
    if (st !== 'Approved') return false;
    if (hidePosted && e.sch_state === 'posted') return false;
    return true;
  }).length;
  const displayCounts = { ...counts, approved: approvedCount };

  const filtered = entries.filter(e=>{
    if (woFilter && !(e.wo_number||'').toLowerCase().includes(woFilter.toLowerCase())) return false;
    const st=e.post_copy?.status||'Draft';
    if (!TAB_CONFIG.find(t=>t.id===tab)?.statuses.includes(st)) return false;
    if (e.event_date) {
      if (yearF && e.event_date.slice(0, 4) !== yearF) return false;
      if (monthsF.size > 0) { const eMonth = parseInt(e.event_date.split('-')[1], 10); if (!monthsF.has(eMonth)) return false; }
    }
    if (tab === 'approved' && hidePosted && e.sch_state === 'posted') return false;
    return true;
  });

  const handleSave         = async (woId,data)  => { await api.put(`/post-copy/${woId}`,data); load(); };
  const handleSubmit       = async (woId)        => { await api.post(`/post-copy/${woId}/submit-internal`); load(); };
  const handleApproveInt   = async (woId)        => { await api.post(`/post-copy/${woId}/approve-internal`); load(); };
  const handleApproveDir   = async (woId)        => { await api.post(`/post-copy/${woId}/approve-directly`); load(); };
  const handleSendToClient = async (woId)        => { await api.post(`/post-copy/${woId}/send-to-client`); load(); };
  const handleApproveClient= async (woId)        => { await api.post(`/post-copy/${woId}/approve-client`); load(); };
  const handleReject       = async (woId,reason,stage) => { await api.post(`/post-copy/${woId}/reject`,{reason,stage}); load(); };

  return (
    <div>
      <div style={{position:'sticky',top:'-24px',zIndex:30,background:'var(--bg)',margin:'-24px -24px 16px',padding:'24px 24px 0'}}>
        {/* Tabs */}
        <div style={{display:'flex',gap:0,marginBottom:'16px',borderBottom:'2px solid var(--border)',alignItems:'flex-end'}}>
          {TAB_CONFIG.filter(t => { const cfg = (OPERATIONS_TABS.postcopy||[]).find(x=>x.id===t.id); return !cfg?.permissionKey || canTab(cfg.permissionKey); }).map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:'8px 20px',fontSize:'0.82rem',fontWeight:700,border:'none',background:'none',cursor:'pointer',color:tab===t.id?t.color:'var(--text3)',borderBottom:tab===t.id?`3px solid ${t.color}`:'3px solid transparent',marginBottom:'-2px'}}>
              {t.label} {displayCounts[t.id]>0 && <span style={{marginLeft:'4px',padding:'1px 6px',background:t.color+'22',color:t.color,borderRadius:'10px',fontSize:'0.7rem',fontWeight:800}}>{displayCounts[t.id]}</span>}
            </button>
          ))}
          <div style={{marginLeft:'auto'}} />
        </div>
        {/* Filters */}
        <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{position:'relative',display:'flex',alignItems:'center'}}>
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--text3)" strokeWidth="1.5" style={{position:'absolute',left:8,pointerEvents:'none'}}><circle cx="6.5" cy="6.5" r="4.5"/><line x1="10" y1="10" x2="14" y2="14"/></svg>
              <input value={woFilter||''} onChange={e=>setWoFilter(e.target.value)}
                placeholder="WO number..." style={{padding:'5px 10px',paddingLeft:26,width:130,fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)'}} />
              {woFilter && <span onClick={()=>setWoFilter('')} style={{position:'absolute',right:8,cursor:'pointer',color:'var(--text3)',fontSize:'0.8rem'}}>✕</span>}
            </div>
            <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={clientF} onChange={e=>setClientF(e.target.value)}>
              <option value="">All Clients</option>
              {clients.filter(c=>c.enabled!==false).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={wftF} onChange={e=>setWftF(e.target.value)}>
              <option value="">All Workflow Types</option>
              {wftypes.map(wt=><option key={wt.id} value={wt.id}>{wt.name}</option>)}
            </select>
            <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={yearF} onChange={e=>setYearF(e.target.value)}>
              <option value="">All Years</option>
              {yearOptions.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            {/* Multi-month dropdown */}
            <div data-month-dd style={{position:'relative'}}>
              <button onClick={()=>setShowMonthDd(v=>!v)}
                style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,cursor:'pointer',
                  display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',
                  background:monthsF.size>0?'#6c58f022':'var(--bg2)',
                  border:`1px solid ${monthsF.size>0?'#6c58f0':'var(--border)'}`,
                  color:monthsF.size>0?'#6c58f0':'var(--text)'}}>
                📅 {monthsF.size===0?'All Months':[...monthsF].sort((a,b)=>a-b).map(m=>MONTH_NAMES[m-1]).join(', ')}
                <span style={{fontSize:'0.6rem',marginLeft:2}}>▾</span>
              </button>
              {showMonthDd&&(
                <div style={{position:'absolute',top:'110%',left:0,zIndex:200,
                  background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,
                  boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:10,minWidth:200}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:4}}>
                    {MONTH_NAMES.map((name,i)=>{const m=i+1;const on=monthsF.has(m);return(
                      <button key={m} onClick={()=>toggleMonth(m)}
                        style={{padding:'5px 8px',fontSize:'0.73rem',fontWeight:on?700:500,
                          borderRadius:6,border:`1px solid ${on?'#6c58f0':'var(--border)'}`,
                          background:on?'#6c58f0':'var(--bg2)',
                          color:on?'#fff':'var(--text2)',cursor:'pointer'}}>
                        {name}
                      </button>
                    );})}
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',marginTop:8,gap:6}}>
                    <button onClick={()=>setMonthsF(new Set())}
                      style={{flex:1,padding:'4px',fontSize:'0.68rem',borderRadius:6,cursor:'pointer',
                        border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text3)'}}>Clear</button>
                    <button onClick={()=>setShowMonthDd(false)}
                      style={{flex:1,padding:'4px',fontSize:'0.68rem',fontWeight:700,borderRadius:6,
                        cursor:'pointer',border:'none',background:'#6c58f0',color:'#fff'}}>Done</button>
                  </div>
                </div>
              )}
            </div>
            {tab === 'approved' && (
              <button onClick={()=>setHidePosted(v=>!v)}
                style={{padding:'5px 12px',fontSize:'0.78rem',fontWeight:600,borderRadius:6,cursor:'pointer',whiteSpace:'nowrap',
                  border:`1px solid ${hidePosted?'var(--border)':'#1db87a'}`,
                  background:hidePosted?'var(--bg2)':'#1db87a22',
                  color:hidePosted?'var(--text2)':'#1db87a'}}>
                {hidePosted ? '👁 Show Posted' : '🙈 Hide Posted'}
              </button>
            )}
        </div>
      </div>

      {/* Right detail panel */}
      {activeWOId && (()=>{
        const aEntry = filtered.find(e=>e.id===activeWOId);
        if(!aEntry) return null;
        return (
          <div style={{position:'fixed',top:60,right:0,bottom:0,width:300,background:'var(--bg2)',borderLeft:'1px solid var(--border)',overflowY:'auto',zIndex:50,boxShadow:'-4px 0 24px rgba(0,0,0,0.12)',padding:16,display:'flex',flexDirection:'column',gap:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <span style={{fontWeight:700,fontSize:'0.85rem',color:'var(--text)'}}>{aEntry.wo_number} · {aEntry.title}</span>
              <button onClick={()=>setActiveWOId(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:18,lineHeight:1}}>✕</button>
            </div>
            <DetailPanel wo={aEntry} sc={aEntry.post_copy||{}} type="postcopy" onClose={()=>setActiveWOId(null)} />
          </div>
        );
      })()}

      {loading ? (
        <div style={{padding:'48px',textAlign:'center',color:'var(--text3)'}}>Loading...</div>
      ) : filtered.length===0 ? (
        <div style={{padding:'48px',textAlign:'center',color:'var(--text3)'}}>
          <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>{EMPTY_MSG[tab]?.icon}</div>
          <div style={{fontWeight:700}}>{EMPTY_MSG[tab]?.msg}</div>
          <div style={{fontSize:'0.78rem',color:'var(--text3)',marginTop:'6px'}}>{EMPTY_MSG[tab]?.sub}</div>
        </div>
      ) : (
        filtered.map(e=>(
          <PostCopyCard key={e.id} entry={e} postCopy={e.post_copy} tab={tab}
            onSave={handleSave} onSubmit={handleSubmit}
            onApproveInternal={handleApproveInt} onApproveDirectly={handleApproveDir}
            onSendToClient={handleSendToClient} onApproveClient={handleApproveClient}
            onReject={handleReject}
            onActivate={()=>setActiveWOId(id=>id===e.id?null:e.id)}
            isActive={activeWOId===e.id} />
        ))
      )}
    </div>
  );
}